'use client'
import Image from 'next/image';
import { useTranslations, useLocale } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import ProjectService from '@/app/services/projects';
import { useRouter } from 'next/navigation';

export default function Hero() {
  const router = useRouter();
  const t = useTranslations('Home');
  const locale = useLocale();
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [typesList, setTypesList] = useState<any[]>([]);
  const [error, setError] = useState<boolean>(false);

  const accentRef = useRef<HTMLDivElement>(null);
  const line1Ref = useRef<HTMLSpanElement>(null);
  const line2Ref = useRef<HTMLSpanElement>(null);
  const line3Ref = useRef<HTMLSpanElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const copyrightRef = useRef<HTMLParagraphElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const scrollLineRef = useRef<HTMLDivElement>(null);

  const updateSelectedType = (newType: string) => {
    localStorage.setItem("gfenetre_type", newType);
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'gfenetre_type',
      newValue: newType,
    }));
  };

  async function getTypes() {
    try {
      const result: any = await ProjectService.getTypesList({ no_pagination: 1 });
      if (result?.data?.success) {
        setTypesList(result?.data?.data);
        setActiveFilter(null)
        updateSelectedType('null')
        setError(false)
      }
    } catch (error: any) {
      setError(true)
    }
  }

  useEffect(() => { getTypes() }, [])
  useEffect(() => { 
    console.log(process.env)               // everything the Node process sees
    console.log(process.env.NEXT_PUBLIC_RANDOMENV)
   }, [])

  useEffect(() => {
    let cancelled = false;
    let ctx: any;
    import("gsap").then(({ gsap }) => {
      if (cancelled) return;
      ctx = gsap.context(() => {
        const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

        tl
          .from(accentRef.current, {
            scaleX: 0,
            transformOrigin: "left center",
            duration: 0.7,
            ease: "power2.inOut",
          })
          .from(
            [line1Ref.current, line2Ref.current, line3Ref.current],
            { y: "110%", duration: 0.9, stagger: 0.13 },
            "-=0.3"
          )
          .from(
            listRef.current ? Array.from(listRef.current.children) : [],
            { opacity: 0, x: -40, duration: 0.65, stagger: 0.1 },
            "-=0.55"
          )
          .from(
            [copyrightRef.current, subtitleRef.current],
            { opacity: 0, y: 18, duration: 0.65, stagger: 0.1 },
            "-=0.45"
          )
          .from(scrollLineRef.current, { opacity: 0, duration: 0.5 }, "-=0.3");

        gsap.to(scrollLineRef.current, {
          opacity: 0.2,
          duration: 1.1,
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut",
          delay: 1.8,
        });
      });
    });

    return () => { cancelled = true; ctx?.revert(); };
  }, []);

  return (
    <section className="relative w-full min-h-screen text-white overflow-hidden flex flex-col justify-between p-6 md:p-12 lg:p-16">
      <div className="absolute inset-0 z-0">
        <Image
          src="/images/hero_bg.jpg"
          alt="Modern Swiss building facade with wooden louvers"
          fill
          priority
          sizes="100vw"
          className="object-cover brightness-75 contrast-125"
        />
        <div className="absolute inset-0 bg-black/20" />
      </div>

      <div className="relative z-10 my-auto max-w-8xl w-full mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 items-center mt-40 md:mt-20">
        <div className="lg:col-span-8 space-y-6 md:space-y-8">
          <div ref={accentRef} className="w-16 h-1.5 bg-[#b91c1c]" />
          <h1 className="text-4xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-semibold tracking-wide uppercase leading-tight">
            <div className="overflow-hidden">
              <span ref={line1Ref} className="block">{t('title1')}</span>
            </div>
            <div className="overflow-hidden">
              <span ref={line2Ref} className="block text-white">{t('title2')} </span>
            </div>
            <div className="overflow-hidden">
              <span ref={line3Ref} className="block text-[#b91c1c]">{t('title3')}</span>
            </div>
          </h1>
        </div>
      </div>

      <div className="relative z-10 max-w-8xl w-full mx-auto pt-4 flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
        <ul ref={listRef} className="space-y-2 md:space-y-3 font-medium tracking-wide text-lg md:text-xl">
          {typesList.map((item) => (
            <li key={item.id} className="group flex items-center gap-0 hover:text-[#b91c1c] transition-colors duration-300 cursor-pointer" 
              onClick={() => { setActiveFilter(item.id); updateSelectedType(item.id); router.push("/projects") }}>
              <span className="block w-0 group-hover:w-5 h-px bg-[#b91c1c] transition-all duration-300 mr-0 group-hover:mr-3" />
              {item[`name_${locale}`]}
            </li>
          ))}
        </ul>
      </div>

      <div className="relative z-10 max-w-8xl w-full mx-auto pt-8 mt-6 border-t border-white/10">
        <div className="w-full flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 sm:gap-12 text-sm text-gray-300 tracking-wide">
          <p ref={copyrightRef}>&copy; {t('your_first_choice_since')}</p>
          <p ref={subtitleRef} className="font-medium text-white pr-4">
            {t('subtitle')}
          </p>
        </div>
      </div>
    </section>
  );
}
